"use client"

import React, { useState, useEffect, useCallback } from "react"
import { Search, Phone, Calendar, User, CheckCircle, XCircle, History, ChevronLeft, ChevronRight } from "lucide-react"
import { Input } from "../../components/ui/input"
import { Badge } from "../../components/ui/badge"
import { Button } from "../../components/ui/button"
import { format } from "date-fns"
import { getCallHistory, OBDCallLog } from "../../services/obdApi"

const AICallAgentPage: React.FC = () => {
  const [callHistory, setCallHistory] = useState<OBDCallLog[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalResults, setTotalResults] = useState(0)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setPage(1) // Reset to first page when search changes
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Format duration from seconds to readable format
  const formatDuration = (seconds?: number): string => {
    if (!seconds || seconds === 0) return "0s"
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`
    }
    return `${remainingSeconds}s`
  }

  // Fetch call history from API
  const fetchCallHistory = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getCallHistory({
        page,
        limit,
        search: debouncedSearchTerm || undefined,
      })
      setCallHistory(response.data.callLogs)
      setTotalPages(response.totalPages)
      setTotalResults(response.totalResults)
    } catch (error) {
      console.error("Error fetching call history:", error)
    } finally {
      setLoading(false)
    }
  }, [page, limit, debouncedSearchTerm])

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchCallHistory()
  }, [fetchCallHistory])

  const getStatusBadge = (status?: string) => {
    if (!status) {
      return (
        <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
          Unknown
        </Badge>
      )
    }

    const statusLower = status.toLowerCase()
    switch (statusLower) {
      case "answered":
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        )
      case "failed":
      case "no answer":
      case "busy":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="w-3 h-3 mr-1" />
            {status}
          </Badge>
        )
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            {status}
          </Badge>
        )
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
            {status}
          </Badge>
        )
    }
  }

  return (
    <div className="bg-background h-full p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-6 h-6 text-[#EC6E51]" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">AI Call Agent</h1>
          </div>
          <p className="text-sm text-gray-600">Manage automated call schedulers and view call history</p>
        </div>

        {/* Search */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
                <div className="flex-1 relative w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by candidate, phone, or scheduler..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
              </div>
          </div>

        {/* Call History */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Call ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Mobile
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Call Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Hangup Cause
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EC6E51] mb-2"></div>
                            <p>Loading call history...</p>
                          </div>
                        </td>
                      </tr>
                    ) : callHistory.length > 0 ? (
                      callHistory.map((call) => (
                        <tr key={call._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-4">
                            <div className="text-sm font-medium text-gray-900">
                              {call.callId || "N/A"}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="w-4 h-4 text-gray-400 mr-2" />
                              {call.mobile || "N/A"}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm text-gray-900">{call.campaignName || call.campaignId || "N/A"}</div>
                            {call.cli && (
                              <div className="text-xs text-gray-500 mt-1">CLI: {call.cli}</div>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-600">
                              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                              {call.timestamp 
                                ? format(new Date(call.timestamp), "MMM dd, yyyy 'at' hh:mm a")
                                : call.callStartTime
                                ? format(new Date(call.callStartTime), "MMM dd, yyyy 'at' hh:mm a")
                                : "N/A"}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDuration(call.duration)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(call.status)}
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-sm text-gray-600 max-w-xs truncate" title={call.hangupCause || ""}>
                              {call.hangupCause || "N/A"}
                            </p>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <History className="w-12 h-12 text-gray-300 mb-2" />
                            <p>No call history found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="text-sm text-gray-700">
                    Showing {callHistory.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, totalResults)} of {totalResults} results
                  </div>
                  <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                      size="sm"
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1 || loading}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            disabled={loading}
                            className={page === pageNum ? "bg-[#EC6E51] text-white hover:bg-[#d65f47]" : ""}
                          >
                            {pageNum}
                </Button>
                        );
                      })}
                    </div>
                    
                <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages || loading}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
                </div>
              )}
            </div>
      </div>
    </div>
  )
}

export default AICallAgentPage
